import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdBusiness, MdAdd, MdSearch, MdSensors, MdDeveloperBoard, MdDelete,
    MdChevronRight, MdPendingActions, MdCheckCircle, MdCancel, MdEmail,
    MdPhone, MdPerson, MdRefresh, MdHourglassEmpty
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddCompanyModal from '../../components/admin/AddCompanyModal';
import { useLanguage } from '../../context/LanguageContext';

export default function CompaniesPage() {
    const { t, language } = useLanguage();
    const [tab, setTab] = useState('companies'); // 'companies' | 'pending'
    const [companies, setCompanies] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingPending, setLoadingPending] = useState(false);
    const [search, setSearch] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [approving, setApproving] = useState(null);
    const [rejecting, setRejecting] = useState(null);
    const [linkCompanyMap, setLinkCompanyMap] = useState({});
    const navigate = useNavigate();

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const res = await api.get('/companies');
            setCompanies(res.data.data || []);
        } catch {
            toast.error(t('companies.loadFailed'));
        } finally {
            setLoadingCompanies(false);
        }
    };

    const fetchPendingRequests = async () => {
        try {
            setLoadingPending(true);
            const res = await api.get('/registrations?status=pending');
            setPendingRequests(res.data.data || []);
        } catch {
            toast.error(t('companies.applicationsFailed'));
        } finally {
            setLoadingPending(false);
        }
    };

    useEffect(() => { fetchCompanies(); fetchPendingRequests(); }, []);

    useEffect(() => {
        if (tab === 'pending') fetchPendingRequests();
    }, [tab]);

    const handleDelete = async (id, name) => {
        if (!window.confirm(t('companies.deleteConfirm', { name }))) return;
        try {
            setDeleting(id);
            await api.delete(`/companies/${id}`);
            toast.success(t('companies.deleted'));
            fetchCompanies();
        } catch {
            toast.error(t('companies.deleteFailed'));
        } finally {
            setDeleting(null);
        }
    };

    const handleApprove = async (req) => {
        const companyId = linkCompanyMap[req._id];
        if (!companyId) {
            toast.error(t('companies.mustSelectCompany'));
            return;
        }
        const targetCompany = companies.find(c => c._id === companyId);
        if (!window.confirm(t('companies.approveConfirm', { username: req.username, companyName: targetCompany?.name || '' }))) return;
        try {
            setApproving(req._id);
            await api.post(`/registrations/${req._id}/approve`, { companyId });
            toast.success(t('companies.approved'));
            fetchPendingRequests();
            // Also refresh companies in case user switches tab
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.message || t('companies.approveFailed'));
        } finally {
            setApproving(null);
        }
    };

    const handleReject = async (req) => {
        if (!window.confirm(t('companies.rejectConfirm', { name: req.companyName }))) return;
        try {
            setRejecting(req._id);
            await api.post(`/registrations/${req._id}/reject`);
            toast.success(t('companies.rejected'));
            fetchPendingRequests();
        } catch (err) {
            toast.error(err.response?.data?.message || t('companies.rejectFailed'));
        } finally {
            setRejecting(null);
        }
    };

    const filtered = companies.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.address.toLowerCase().includes(search.toLowerCase())
    );

    const dateLocale = language === 'tr' ? 'tr-TR' : 'en-US';

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">{t('companies.title')}</h1>
                    <p className="page-subtitle">
                        {tab === 'companies'
                            ? t('companies.registeredCount', { count: companies.length })
                            : t('companies.pendingCount', { count: pendingRequests.length })
                        }
                    </p>
                </div>
                <div className="page-header-actions">
                    {tab === 'companies' && (
                        <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)} id="add-company-btn">
                            <MdAdd /> {t('companies.addCompany')}
                        </button>
                    )}
                    {tab === 'pending' && (
                        <button className="btn btn-secondary btn-sm" onClick={fetchPendingRequests} id="refresh-pending-btn">
                            <MdRefresh /> {t('common.refresh')}
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
                <TabButton
                    id="tab-companies"
                    active={tab === 'companies'}
                    onClick={() => setTab('companies')}
                    icon={<MdBusiness />}
                    label={t('companies.tabCompanies')}
                    count={companies.length}
                />
                <TabButton
                    id="tab-pending"
                    active={tab === 'pending'}
                    onClick={() => setTab('pending')}
                    icon={<MdPendingActions />}
                    label={t('companies.tabPending')}
                    count={pendingRequests.length}
                    highlight={pendingRequests.length > 0}
                />
            </div>

            <AnimatePresence mode="wait">
                {tab === 'companies' ? (
                    <motion.div key="companies" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        {/* Search */}
                        <div style={{ position: 'relative', marginBottom: 20 }}>
                            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 18 }}>
                                <MdSearch />
                            </span>
                            <input
                                className="form-input"
                                style={{ paddingLeft: 42 }}
                                placeholder={t('companies.searchPlaceholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                id="search-companies"
                            />
                        </div>

                        <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                                        <MdBusiness />
                                    </div>
                                    {t('companies.allCompanies')}
                                </div>
                            </div>
                            <div style={{ padding: 0 }}>
                                {loadingCompanies ? (
                                    <div className="loading-container"><div className="spinner"></div></div>
                                ) : filtered.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-icon">🏢</div>
                                        <div className="empty-state-title">{search ? t('companies.noResults') : t('companies.noCompanies')}</div>
                                        <div className="empty-state-desc">{search ? t('companies.tryDifferentSearch') : t('companies.clickToAdd')}</div>
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>{t('companies.companyName')}</th>
                                                    <th>{t('companies.contact')}</th>
                                                    <th>{t('companies.systemCount')}</th>
                                                    <th>{t('companies.sensorCount')}</th>
                                                    <th>{t('companies.createdAt')}</th>
                                                    <th style={{ width: 160 }}>{t('common.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.map((company, i) => {
                                                    const sensorCount = (company.systems || []).reduce((s, sys) => s + (sys.sensors?.length || 0), 0);
                                                    const counterCount = (company.systems || []).reduce((s, sys) => s + (sys.counters?.length || 0), 0);
                                                    return (
                                                        <motion.tr key={company._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }}>
                                                            <td>
                                                                <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <div style={{
                                                                        width: 32, height: 32, borderRadius: 8,
                                                                        background: `hsl(${(i * 47) % 360}, 60%, 25%)`,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: 13, fontWeight: 700, color: `hsl(${(i * 47) % 360}, 80%, 75%)`
                                                                    }}>
                                                                        {company.name[0].toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div>{company.name}</div>
                                                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{company.address}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontSize: 12 }}>
                                                                    {company.email ? <div style={{ color: 'var(--primary)' }}>{company.email}</div> : <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('common.noEmail')}</div>}
                                                                    {company.telNo ? <div>{company.telNo}</div> : <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('common.noPhone')}</div>}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-primary">
                                                                    <MdDeveloperBoard /> {company.systems?.length || 0}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                    <span className="badge badge-primary">
                                                                        <MdSensors /> {sensorCount}
                                                                    </span>
                                                                    <span className="badge badge-warning">
                                                                        <MdDeveloperBoard /> {counterCount}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                                {new Date(company.createdAt).toLocaleDateString(dateLocale)}
                                                            </td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: 8 }}>
                                                                    <button
                                                                        className="btn btn-secondary btn-sm"
                                                                        onClick={() => navigate(`/admin/companies/${company._id}`)}
                                                                        id={`view-company-${company._id}`}
                                                                    >
                                                                        <MdChevronRight /> {t('common.detail')}
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-danger btn-sm btn-icon"
                                                                        onClick={() => handleDelete(company._id, company.name)}
                                                                        disabled={deleting === company._id}
                                                                        id={`delete-company-${company._id}`}
                                                                    >
                                                                        {deleting === company._id ? <span className="spinner spinner-sm" /> : <MdDelete />}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </motion.tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                ) : (
                    <motion.div key="pending" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="card-title-icon" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>
                                        <MdPendingActions />
                                    </div>
                                    {t('companies.pendingTitle')}
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                    {t('companies.adminApprovalRequired')}
                                </span>
                            </div>
                            <div style={{ padding: 0 }}>
                                {loadingPending ? (
                                    <div className="loading-container"><div className="spinner"></div></div>
                                ) : pendingRequests.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-icon"><MdHourglassEmpty style={{ fontSize: 48, opacity: 0.3 }} /></div>
                                        <div className="empty-state-title">{t('companies.noPending')}</div>
                                        <div className="empty-state-desc">{t('companies.pendingDesc')}</div>
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>{t('companies.companyHintLabel')}</th>
                                                    <th>{t('companies.contact')}</th>
                                                    <th>{t('register.username')}</th>
                                                    <th>{t('companies.applicationDate')}</th>
                                                    <th style={{ width: 220 }}>{t('companies.selectCompanyToLink')}</th>
                                                    <th style={{ width: 180 }}>{t('common.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingRequests.map((req, i) => (
                                                    <motion.tr
                                                        key={req._id}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: i * 0.05 }}
                                                    >
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <div style={{
                                                                    width: 36, height: 36, borderRadius: 10,
                                                                    background: `hsl(${(i * 67) % 360}, 55%, 20%)`,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: 14, fontWeight: 700,
                                                                    color: `hsl(${(i * 67) % 360}, 75%, 70%)`,
                                                                    flexShrink: 0
                                                                }}>
                                                                    {req.companyName[0].toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>
                                                                        {req.companyName}
                                                                    </div>
                                                                    <span className="badge badge-warning" style={{ fontSize: 10, padding: '2px 7px', marginTop: 3 }}>
                                                                        {t('companies.pendingApproval')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--primary)' }}>
                                                                    <MdEmail style={{ fontSize: 13, flexShrink: 0 }} />
                                                                    {req.email}
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
                                                                    <MdPhone style={{ fontSize: 13, flexShrink: 0 }} />
                                                                    {req.phone}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: 13 }}>
                                                                <MdPerson style={{ fontSize: 15 }} />
                                                                <span style={{ fontFamily: 'monospace' }}>{req.username}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>
                                                            {new Date(req.createdAt).toLocaleDateString(dateLocale)}
                                                            <div style={{ fontSize: 11 }}>
                                                                {new Date(req.createdAt).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="form-input"
                                                                style={{ fontSize: 12, padding: '6px 10px' }}
                                                                value={linkCompanyMap[req._id] || ''}
                                                                onChange={e => setLinkCompanyMap(m => ({ ...m, [req._id]: e.target.value }))}
                                                                id={`link-company-${req._id}`}
                                                            >
                                                                <option value="">{t('companies.selectCompanyPlaceholder')}</option>
                                                                {companies.map(c => (
                                                                    <option key={c._id} value={c._id}>{c.name}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <button
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => handleApprove(req)}
                                                                    disabled={approving === req._id || rejecting === req._id}
                                                                    id={`approve-reg-${req._id}`}
                                                                    style={{ flex: 1, justifyContent: 'center' }}
                                                                >
                                                                    {approving === req._id
                                                                        ? <span className="spinner spinner-sm" />
                                                                        : <><MdCheckCircle /> {t('companies.approve')}</>
                                                                    }
                                                                </button>
                                                                <button
                                                                    className="btn btn-danger btn-sm btn-icon"
                                                                    onClick={() => handleReject(req)}
                                                                    disabled={approving === req._id || rejecting === req._id}
                                                                    id={`reject-reg-${req._id}`}
                                                                    title={t('common.reject')}
                                                                >
                                                                    {rejecting === req._id
                                                                        ? <span className="spinner spinner-sm" />
                                                                        : <MdCancel />
                                                                    }
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {showAddModal && (
                <AddCompanyModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => { setShowAddModal(false); fetchCompanies(); }}
                />
            )}
        </div>
    );
}

function TabButton({ id, active, onClick, icon, label, count, highlight }) {
    return (
        <button
            id={id}
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 18px',
                background: 'none',
                border: 'none',
                borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
                color: active ? 'var(--primary)' : 'var(--text-muted)',
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: -1,
                whiteSpace: 'nowrap'
            }}
        >
            {icon}
            {label}
            {count > 0 && (
                <span style={{
                    background: highlight && !active
                        ? 'rgba(245,158,11,0.2)'
                        : active ? 'rgba(79,142,247,0.15)' : 'var(--bg-elevated)',
                    color: highlight && !active ? 'var(--warning)' : active ? 'var(--primary)' : 'var(--text-muted)',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '1px 7px',
                    minWidth: 20,
                    textAlign: 'center',
                    border: highlight && !active ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent'
                }}>
                    {count}
                </span>
            )}
        </button>
    );
}
